import { useEffect, useReducer, useRef } from 'react';

import type { ResearchRoomMessage } from '@repo/contracts';

/**
 * How long a persona "types" before their answer shows, by its length —
 * long enough that the last answer has been read before the next lands,
 * short enough that a round never drags.
 */
export function typingMs(text: string): number {
  return Math.min(
    MAX_TYPING_MS,
    Math.max(MIN_TYPING_MS, 500 + text.length * 14),
  );
}
const MIN_TYPING_MS = 1100;
const MAX_TYPING_MS = 3600;
/** The beat between one message landing and the next person starting. */
export const GAP_MS = 650;
/** Floto's read of the round, once the room has spoken. */
const FLOTO_MS = 900;
/** A read that arrives after its beat has passed: shown almost at once. */
const QUICK_READ_MS = 300;
/**
 * Floto's read lands within 2s of the round's last answer, or not at all
 * in the moment — a read that turns up after the reader has moved on
 * interrupts more than it helps. It is still in the thread on reopening,
 * and its suggested questions still reach the ideas.
 */
export const READ_WITHIN_MS = 2000;
/** A hello is one short line: a beat of typing, not an answer's worth. */
const MAX_INTRO_MS = 1300;
/**
 * The pause after a hello before the next person starts typing — long enough
 * to read who they are, which is the point of the introductions. Also the
 * pause before Floto's opener, after the last of them.
 */
export const INTRO_READ_MS = 2600;
/** A beat after Floto says the panel is coming in, before the first hello. */
const FIRST_INTRO_MS = 900;
/**
 * A room's hellos are written with the room itself, so they arrive in the
 * same response as its id — and everything there on opening is history,
 * shown at once. Hellos this fresh are not history: the room was just opened
 * in front of the reader, who should watch the panel arrive.
 */
const FRESH_INTRO_MS = 30_000;

/** What the reader is waiting on: who is typing, and since when. */
export interface Stage {
  message: ResearchRoomMessage;
  /** Its words have arrived — the wait left is only the typing. */
  ready: boolean;
  /** When the typing shows; before that, a beat of nothing. */
  showsAt: number;
  /** When the message itself shows, if it is ready. */
  revealsAt: number;
}

/** Messages that arrive one at a time, like a group chat: persona answers,
 *  and a read Floto speaks up with. Everything else — the question, Floto
 *  holding one back, a poll's card — shows when it reaches the front. */
export function isPaced(message: ResearchRoomMessage): boolean {
  if (message.kind === 'answer') return true;
  if (message.kind === 'intro') return message.speakerKind === 'persona';
  // Waits its turn after the hellos, rather than landing under the last one
  // before it has been read. A room's opener on reopening is already shown.
  if (message.kind === 'opener') return true;
  return message.kind === 'summary' && !message.quiet;
}

/**
 * The thread as the reader sees it: everything up to the first paced message
 * not yet shown; that one, as someone typing; nothing after it.
 *
 * A message waits for its words (`status` no longer pending), and in a round
 * answered separately for everyone's — nobody's answer is read before the
 * rest are in — and then for its typing time.
 */
export function paceThread(
  messages: ResearchRoomMessage[],
  shown: ReadonlySet<string>,
  timing: {
    /** When the current stage took the front; null if it has not yet. */
    stageSince: number | null;
    /** When the last message was revealed. */
    lastRevealAt: number;
    now: number;
    /** When each paced message was revealed, where it was seen arriving. */
    revealedAt?: ReadonlyMap<string, number>;
  },
): {
  /** Floto's reads that came too late to show — see `READ_WITHIN_MS`. */
  late: string[];
  stage: Stage | null;
  visible: ResearchRoomMessage[];
} {
  const visible: ResearchRoomMessage[] = [];
  const late: string[] = [];
  for (const message of messages) {
    if (shown.has(message.id) || !isPaced(message)) {
      visible.push(message);
      continue;
    }
    // When this round's last answer showed, if it was seen showing.
    const answeredAt =
      message.kind === 'summary'
        ? Math.max(
            -1,
            ...messages
              .filter((m) => m.round === message.round && m.kind === 'answer')
              .map((m) => timing.revealedAt?.get(m.id) ?? -1),
          )
        : -1;
    const arrived = timing.stageSince ?? timing.now;
    if (
      answeredAt >= 0 &&
      arrived + QUICK_READ_MS > answeredAt + READ_WITHIN_MS
    ) {
      late.push(message.id);
      visible.push(message);
      continue;
    }
    const sealed =
      message.kind === 'answer' &&
      messages.some(
        (m) =>
          m.round === message.round &&
          m.kind === 'question' &&
          m.independent === true,
      ) &&
      messages.some(
        (m) =>
          m.round === message.round &&
          m.kind === 'answer' &&
          m.status === 'pending',
      );
    const ready = message.status !== 'pending' && !sealed;
    const since = timing.stageSince ?? timing.now;
    const opening = message.kind === 'intro' || message.kind === 'opener';
    const showsAt =
      opening && timing.lastRevealAt === 0
        ? since + FIRST_INTRO_MS
        : Math.max(
            since,
            timing.lastRevealAt + (opening ? INTRO_READ_MS : GAP_MS),
          );
    const typing =
      message.kind === 'summary'
        ? // On time, it takes its beat; behind it, it shows almost at once.
          since <= timing.lastRevealAt + GAP_MS
          ? FLOTO_MS
          : QUICK_READ_MS
        : message.kind === 'opener'
          ? FLOTO_MS
          : message.kind === 'intro'
            ? Math.min(MAX_INTRO_MS, typingMs(message.text))
            : typingMs(message.text);
    return {
      late,
      stage: { message, ready, revealsAt: showsAt + typing, showsAt },
      visible,
    };
  }
  return { late, stage: null, visible };
}

/**
 * Paces a room's thread for reading. What was already there when the room
 * opened shows at once; what arrives after plays out one message at a time.
 *
 * Client-side only: the server answers as fast as it can, and the stream and
 * cache stay exactly as they were — this decides only when each line is
 * drawn.
 */
export function usePacedThread(
  roomId: string | undefined,
  messages: ResearchRoomMessage[],
): {
  /** Floto's reads that came too late to show in the moment. */
  muted: ReadonlySet<string>;
  stage: Stage | null;
  /** Still playing out — the composer waits for the room to finish. */
  playing: boolean;
  visible: ResearchRoomMessage[];
} {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const seededFor = useRef<string | undefined>(undefined);
  const shown = useRef<Set<string>>(new Set());
  const revealedAt = useRef<Map<string, number>>(new Map());
  const muted = useRef<Set<string>>(new Set());
  const stageRef = useRef<{ id: string; since: number } | null>(null);
  const lastRevealAt = useRef(0);

  // The room as it was on opening is history: no replaying it. A round
  // still running when it opened is paced from where it is.
  if (roomId && seededFor.current !== roomId) {
    seededFor.current = roomId;
    const fresh = Date.now() - FRESH_INTRO_MS;
    shown.current = new Set(
      messages
        .filter((m) => m.status !== 'pending')
        .filter(
          (m) =>
            !(
              (m.kind === 'intro' || m.kind === 'opener') &&
              Date.parse(m.createdAt) > fresh
            ),
        )
        .map((m) => m.id),
    );
    stageRef.current = null;
    lastRevealAt.current = 0;
    revealedAt.current = new Map();
    muted.current = new Set();
  }

  const now = Date.now();
  const { late, stage, visible } = paceThread(messages, shown.current, {
    lastRevealAt: lastRevealAt.current,
    now,
    revealedAt: revealedAt.current,
    stageSince:
      stageRef.current && stageRef.current.id === stageId(messages, shown)
        ? stageRef.current.since
        : null,
  });
  for (const id of late) {
    shown.current.add(id);
    muted.current.add(id);
  }
  if (stage && stageRef.current?.id !== stage.message.id) {
    stageRef.current = { id: stage.message.id, since: now };
  }
  if (!stage) stageRef.current = null;

  // Which step of the plan this is: waiting for the typing to show, then
  // waiting for the words and the typing to finish. Each step sets its own
  // timer, so the key has to change between them.
  const stageKey = stage
    ? `${stage.message.id}:${String(stage.ready)}:${String(stage.showsAt)}:${String(now >= stage.showsAt)}`
    : '';
  useEffect(() => {
    if (!stage) return;
    const wait = (at: number) => Math.max(0, at - Date.now());
    // Before the typing shows: wake when it does.
    if (Date.now() < stage.showsAt) {
      const timer = setTimeout(rerender, wait(stage.showsAt));
      return () => {
        clearTimeout(timer);
      };
    }
    // Typing, and the words are in: reveal when the typing is done. Not
    // ready yet: the next stream frame re-renders and brings us back here.
    if (!stage.ready) return;
    const timer = setTimeout(() => {
      shown.current.add(stage.message.id);
      lastRevealAt.current = Date.now();
      revealedAt.current.set(stage.message.id, lastRevealAt.current);
      stageRef.current = null;
      rerender();
    }, wait(stage.revealsAt));
    return () => {
      clearTimeout(timer);
    };
    // Keyed on what changes the plan, not on the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageKey]);

  return { muted: muted.current, playing: stage !== null, stage, visible };
}

/** The id of the first paced message not yet shown, if any. */
function stageId(
  messages: ResearchRoomMessage[],
  shown: { current: ReadonlySet<string> },
): string | undefined {
  return messages.find((m) => isPaced(m) && !shown.current.has(m.id))?.id;
}
